import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useStats } from '@/hooks/use-admin'
import {
  BarChart3,
  MessageSquare,
  MessagesSquare,
  Smartphone,
  TrendingUp,
  UserCheck,
  Users,
} from 'lucide-react'

export function AdminDashboardPage() {
  const { data: stats, isLoading } = useStats()

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your platform metrics</p>
      </div>

      {/* User Stats Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Users</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_users ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                {stats?.new_users_this_month ?? 0} new this month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.active_users ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                {stats?.total_users
                  ? Math.round((stats.active_users / stats.total_users) * 100)
                  : 0}
                % of total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">New This Week</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.new_users_this_week ?? 0}</div>
              <p className="text-xs text-muted-foreground">{stats?.new_users_today ?? 0} today</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Engaged Users</CardTitle>
              <MessagesSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.users_with_messages ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                {stats?.total_users
                  ? Math.round(((stats.users_with_messages ?? 0) / stats.total_users) * 100)
                  : 0}
                % have chatted
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Engagement Stats Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Engagement</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_messages ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                {stats?.messages_this_month ?? 0} this month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Messages This Week</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.messages_this_week ?? 0}</div>
              <p className="text-xs text-muted-foreground">{stats?.messages_today ?? 0} today</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
              <MessagesSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_chats ?? 0}</div>
              <p className="text-xs text-muted-foreground">Web chat sessions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Messages/User</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.avg_messages_per_user ?? 0}</div>
              <p className="text-xs text-muted-foreground">For engaged users</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Breakdown Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Auth Method Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Users by Authentication Method</CardTitle>
            <CardDescription>Distribution of users across auth methods</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-8">
              {stats?.users_by_auth_method &&
                Object.entries(stats.users_by_auth_method).map(([method, count]) => (
                  <div key={method} className="text-center">
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-sm text-muted-foreground capitalize">{method}</div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Channel Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Messages by Channel</CardTitle>
            <CardDescription>Distribution of messages across channels</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-8">
              {stats?.messages_by_channel &&
                Object.entries(stats.messages_by_channel).map(([channel, count]) => (
                  <div key={channel} className="text-center flex flex-col items-center">
                    {channel === 'sms' && (
                      <Smartphone className="h-5 w-5 text-muted-foreground mb-1" />
                    )}
                    {channel === 'web' && (
                      <MessageSquare className="h-5 w-5 text-muted-foreground mb-1" />
                    )}
                    {channel === 'voice' && (
                      <MessagesSquare className="h-5 w-5 text-muted-foreground mb-1" />
                    )}
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-sm text-muted-foreground uppercase">{channel}</div>
                  </div>
                ))}
              {(!stats?.messages_by_channel ||
                Object.keys(stats.messages_by_channel).length === 0) && (
                <div className="text-muted-foreground">No messages yet</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admins Card */}
      <Card>
        <CardHeader>
          <CardTitle>Admin Accounts</CardTitle>
          <CardDescription>Superuser accounts with admin access</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats?.superusers ?? 0}</div>
          <p className="text-sm text-muted-foreground">
            {stats?.superusers === 1
              ? '1 admin account'
              : `${stats?.superusers ?? 0} admin accounts`}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
